import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useAuthUserState } from './useAuthUserState'
import { getUserTenants, type TenantEntry } from '../services/schemas'
import { logErrorToService } from '../utils/errorLogger'

interface UserTenantsState {
  isLoading: boolean
  schemas: TenantEntry[]
  error: string | null
}

const SCHEMAS_RETRY_DELAYS_MS = [150, 400, 900]

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

async function fetchUserTenantsWithRetry(userId: string): Promise<TenantEntry[]> {
  const attempts = SCHEMAS_RETRY_DELAYS_MS.length + 1

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const schemas = await getUserTenants(userId)
    if (schemas.length > 0) {
      if (attempt > 0) {
        Sentry.addBreadcrumb({
          category: 'auth-bootstrap',
          level: 'info',
          message: 'Visible schemas resolved after startup retry',
          data: { attempt: attempt + 1, userId },
        })
      }
      return schemas
    }

    if (attempt < SCHEMAS_RETRY_DELAYS_MS.length) {
      await wait(SCHEMAS_RETRY_DELAYS_MS[attempt])
    }
  }

  return []
}

/**
 * Loads the list of schemas visible to the current user from the
 * `vaultbi.user_tenants` view. Depends on `useAuthUserState` for auth
 * state and internal user id resolution.
 */
export function useUserTenants(): UserTenantsState {
  const { isLoading: authLoading, isAuthenticated, isTokenReady, userId } = useAuthUserState()
  const [state, setState] = useState<UserTenantsState>({
    isLoading: false,
    schemas: [],
    error: null,
  })

  useEffect(() => {
    if (!isAuthenticated || !isTokenReady || !userId) return

    let cancelled = false

    queueMicrotask(() => {
      if (!cancelled) {
        setState((prev) => ({ isLoading: true, schemas: prev.schemas, error: null }))
      }
    })

    fetchUserTenantsWithRetry(userId)
      .then((schemas) => {
        if (!cancelled) setState({ isLoading: false, schemas, error: null })
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          logErrorToService(err, { context: 'useUserTenants', severity: 'high' })
          const message = err instanceof Error ? err.message : 'Failed to load schemas'
          setState((prev) => ({ isLoading: false, schemas: prev.schemas, error: message }))
        }
      })

    return () => { cancelled = true }
  }, [isAuthenticated, isTokenReady, userId])

  if (!isAuthenticated || !userId) {
    return { isLoading: authLoading, schemas: [], error: null }
  }

  return state
}
